import type { WorkspaceBillingContract } from '@nest-react/domain';
import {
  type ReactElement,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { requestWithActiveRegionalClient } from '~/app/regionalApiClient';
import { createBillingRepository } from '~/repositories/billingRepository';
import {
  emptyBillingSnapshot,
  useActiveWorkspace,
  useWorkspaceStore,
} from '~/stores/workspace';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { BillingDialogs } from './BillingDialogs';
import {
  hasActivePaidSubscription,
  hasExpiredPaidSubscription,
} from './generationBillingAccess';

const repository = createBillingRepository(requestWithActiveRegionalClient);

interface UpgradeRequest {
  requiredCredits?: string | null;
  reason?: 'subscription' | 'credits';
  feature?: 'watermark';
}

interface DialogRequest extends UpgradeRequest {
  summary?: WorkspaceBillingContract;
  availableCredits?: string;
}

interface BillingUpgradeValue {
  openUpgrade: (request?: UpgradeRequest) => void;
  checkGenerationAccess: (requiredCredits?: string | null) => Promise<boolean>;
}

const BillingUpgradeContext = createContext<BillingUpgradeValue>({
  openUpgrade: () => undefined,
  checkGenerationAccess: async () => true,
});

export const useBillingUpgrade = (): BillingUpgradeValue =>
  useContext(BillingUpgradeContext);

const EMPTY_BILLING_SNAPSHOT = emptyBillingSnapshot();

export const BillingUpgradeProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const { t } = useTranslation('billing');
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.id;
  const [upgradeRequest, setUpgradeRequest] = useState<DialogRequest | null>(
    null
  );
  const fetchWorkspaceBillingSummary = useWorkspaceStore(
    state => state.fetchWorkspaceBillingSummary
  );
  const fetchWorkspaceWallet = useWorkspaceStore(
    state => state.fetchWorkspaceWallet
  );
  const billingSnapshot = useWorkspaceStore(
    useShallow(state =>
      workspaceId
        ? (state.billingByWorkspace[workspaceId] ?? EMPTY_BILLING_SNAPSHOT)
        : EMPTY_BILLING_SNAPSHOT
    )
  );

  const openUpgrade = useCallback(
    (next: UpgradeRequest = {}) => {
      if (!workspaceId) return;
      setUpgradeRequest(next);
      void fetchWorkspaceBillingSummary(workspaceId);
      if (next.requiredCredits) {
        void fetchWorkspaceWallet(workspaceId);
      }
    },
    [fetchWorkspaceBillingSummary, fetchWorkspaceWallet, workspaceId]
  );

  const checkGenerationAccess = useCallback(
    async (requiredCredits?: string | null): Promise<boolean> => {
      if (!workspaceId) return false;
      // Refresh at submission: an open tab can outlive its paid term or balance.
      const summary = await repository.getSummary(workspaceId);
      if (hasExpiredPaidSubscription(summary.subscription)) {
        setUpgradeRequest({ reason: 'subscription', summary });
        return false;
      }
      const wallet = await repository.getWallet(workspaceId);
      const available = Number(wallet.availableBalance ?? wallet.balance);
      if (
        available <= 0 ||
        (requiredCredits && available < Number(requiredCredits))
      ) {
        setUpgradeRequest({
          reason: 'credits',
          requiredCredits,
          summary,
          availableCredits: wallet.availableBalance ?? wallet.balance,
        });
        return false;
      }
      return true;
    },
    [workspaceId]
  );

  const close = (): void => {
    setUpgradeRequest(null);
  };

  const loading =
    Boolean(upgradeRequest) &&
    !upgradeRequest?.summary &&
    (billingSnapshot.summaryLoading ||
      (upgradeRequest?.requiredCredits
        ? billingSnapshot.walletLoading
        : false));
  const error = billingSnapshot.summaryError ?? '';
  const summary = upgradeRequest?.summary ?? billingSnapshot.summary;
  const availableCredits =
    upgradeRequest?.availableCredits ?? billingSnapshot.balance;

  return (
    <BillingUpgradeContext.Provider
      value={{ openUpgrade, checkGenerationAccess }}
    >
      {children}
      {summary && upgradeRequest ? (
        <BillingDialogs
          dialog={
            (upgradeRequest.reason === 'credits' ||
              upgradeRequest.requiredCredits) &&
            upgradeRequest.reason !== 'subscription' &&
            summary.workspace.dataRegion === 'RU' &&
            hasActivePaidSubscription(summary.subscription)
              ? 'credits'
              : 'upgrade'
          }
          watermarkUpgrade={upgradeRequest.feature === 'watermark'}
          renewalRequired={hasExpiredPaidSubscription(summary.subscription)}
          insufficientCredits={
            upgradeRequest.requiredCredits
              ? {
                  available: availableCredits ?? undefined,
                  required: upgradeRequest.requiredCredits,
                }
              : undefined
          }
          onClose={close}
          onCheckout={async (plan, billingCadence) => {
            if (!activeWorkspace) throw new Error(t('chooseWorkspace'));
            const checkout = await repository.createYooKassaCheckout(
              activeWorkspace.id,
              plan,
              billingCadence,
              crypto.randomUUID()
            );
            if (checkout.action === 'RESOLVE_PENDING') return checkout;
            if (!checkout.confirmationUrl) throw new Error(t('checkoutError'));
            window.location.assign(checkout.confirmationUrl);
            return null;
          }}
          onAbandonCheckout={async orderId => {
            if (!activeWorkspace) throw new Error(t('chooseWorkspace'));
            return repository.abandonYooKassaOrder(activeWorkspace.id, orderId);
          }}
          onCreditCheckout={async () => {
            if (!activeWorkspace) throw new Error(t('chooseWorkspace'));
            const checkout = await repository.createYooKassaCreditCheckout(
              activeWorkspace.id,
              crypto.randomUUID()
            );
            if (!checkout.confirmationUrl) throw new Error(t('checkoutError'));
            window.location.assign(checkout.confirmationUrl);
          }}
          summary={summary}
        />
      ) : (
        <Dialog
          onOpenChange={open => !open && close()}
          open={upgradeRequest !== null}
        >
          <DialogContent aria-label={t('upgradeYourPlan')}>
            <DialogHeader>
              <DialogTitle>{t('upgradeYourPlan')}</DialogTitle>
              <DialogDescription>
                {loading
                  ? t('loadingPlans')
                  : t('plansLoadError', { message: error || t('loadError') })}
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}
    </BillingUpgradeContext.Provider>
  );
};
